import { useMemo, useState } from 'react'
import { Box, ButtonBase, InputBase, Typography, styled } from '@mui/material'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import { usePrescriptionsQuery } from '../../../features/prescription'
import { SHORT_SERVICE_NAMES } from '../../../constants/const'
import { BrandMark } from './BrandMark'
import { Prescription } from '../../../types/models/Presc'
import { formatTimeInJP } from '../../../helpers'
import { localStorageWrapper } from '../../../helpers/localStorage'
import { colorTokens } from '../../../shared/styles/theme'
import { RADIUS } from '../../../shared/styles/radii'
import { LAYOUT } from '../../../shared/styles/spacing'
import { FONT_TABULAR, typeScale } from '../../../shared/styles/typography'
import { getBrand } from './theme/brandByUser'
import {
  foldByOcrRequestEvent,
  getPatientName,
  groupByDate,
  isQrOnly,
  matchesSearch,
} from './list/queue'
import { isSunkRow, rowBadge } from './remoteInput/model'
import { RemoteFilterTabs } from './remoteInput/RemoteFilterTabs'
import { RouteToggle } from './remoteInput/RouteToggle'
import {
  useRemoteInput,
  useRemoteInputActions,
} from './remoteInput/useRemoteInput'

const Container = styled(Box)({
  width: LAYOUT.sidebarWidth,
  flexShrink: 0,
  background: colorTokens.bg.subtle,
  borderRight: `1px solid ${colorTokens.border.default}`,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'sticky',
  top: 0,
  // 折りたたみトグルは hover でだけ出す(03-patterns/hover-only-chrome.md)
  '&:hover .collapse-toggle': { opacity: 1 },
})

const WorkspaceRow = styled(Box)({
  height: LAYOUT.sidebarWorkspaceRowHeight,
  padding: '0 12px',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 13,
  fontWeight: 600,
  color: colorTokens.fg.default,
})

const RowList = styled('ul')({ listStyle: 'none', margin: 0, padding: 0 })

const Row = styled('li', {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'accent',
})<{ selected: boolean; accent?: string }>(({ selected, accent }) => ({
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: LAYOUT.sidebarRowHeight,
  padding: '0 8px',
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  // 行の地は共有の Sidebar 行と同じ(design-book sidebar.md): 選択 = bg.muted / hover = bg.emphasis。
  background: selected ? colorTokens.bg.muted : 'transparent',
  // カンパニーカラー登録ユーザー(brandByUser)のみ選択行に左端インジケータを敷く。
  // inset なので padding はズレない(box-shadow は layout box の外)。地は accent 化しない
  // (バッジ・左端インジケータで十分。松田 8/30「患者選択の背景はもう無くして良い」)。
  boxShadow: selected && accent ? `inset 3px 0 0 ${accent}` : 'none',
  '&:hover': {
    background: selected ? colorTokens.bg.muted : colorTokens.bg.emphasis,
  },
}))

/**
 * 行頭 8px ドット。**色相を使わず塗り / 輪郭の二値**で示す
 * (00-principles/04-color-discipline.md §4「未着手・対象外は outline」)。
 * 突合の対象 = 塗り / QR直読(対象外)と遠隔入力の入力済(終わった) = 輪郭。完了の緑は使わない。
 */
const Dot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'filled',
})<{ filled: boolean }>(({ filled }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  ...(filled
    ? { background: colorTokens.fg.faint }
    : { border: `1px solid ${colorTokens.fg.disabled}` }),
}))

const FooterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: LAYOUT.sidebarRowHeight,
  margin: '0 8px 8px',
  padding: '0 8px',
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  fontSize: 13,
  color: colorTokens.fg.muted,
  '&:hover': { background: colorTokens.bg.emphasis },
})

const PrescriptionRow = ({
  presc,
  isSelected,
  onSelect,
  accent,
  badge,
  isSunk,
}: {
  presc: Prescription
  isSelected: boolean
  onSelect: (ocrRequestEventId: string) => void
  accent?: string
  /** 患者名の右の 12px の語 1 つ(遠隔入力の札、または頼む店の呼び名)。無ければ出さない */
  badge?: string
  /** 行ごと沈める(輪郭のドット + fg.muted)。QR 直読と遠隔入力の入力済 */
  isSunk: boolean
}) => (
  <Row
    selected={isSelected}
    accent={accent}
    onClick={() => onSelect(presc.ocr_request_event_id)}
  >
    <Dot filled={!isSunk} />
    <Typography
      noWrap
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: 13,
        color: isSunk ? colorTokens.fg.muted : colorTokens.fg.default,
      }}
    >
      {getPatientName(presc)}
    </Typography>
    {badge && (
      <Typography
        noWrap
        sx={{
          maxWidth: 72,
          flexShrink: 0,
          fontSize: 12,
          color: colorTokens.fg.muted,
        }}
      >
        {badge}
      </Typography>
    )}
    <Typography
      sx={{
        fontSize: 12,
        ...FONT_TABULAR,
        color: colorTokens.fg.muted,
        flexShrink: 0,
      }}
    >
      {formatTimeInJP(presc.request_event_created_at)}
    </Typography>
  </Row>
)

interface Props {
  selectedId: string | null
  onSelect: (ocrRequestEventId: string) => void
  onCollapse: () => void
}

/**
 * app shell の Sidebar(224px)。**処方一覧をここに置く**(松田 8/29「患者の一覧は格納可能に。
 * yakureki-front 参照」)。面は詳細 1 つになり、Main に一覧の面は持たない(一覧は 1 か所)。
 *
 * **1行 = 1 OCRRequestEvent。空のレコードも過分割も隠さない**(ADR-0029)。薬剤師はスキャンした
 * 枚数を知っているので、件数が合わないと「失敗したのか / 処理中か / 消えたのか」を区別できない。
 * 確定フィルタも要確認フィルタも件数カウンタも置かず、検索だけインラインで持つ(判断5)。
 * 件数は検索がアクティブな時だけ出す(03-patterns/list-and-status.md)。
 *
 * **行に出すのは 患者名 / 時刻 / QR 直読かどうか の 3 つ**に、遠隔入力の語 1 つが加わる。
 * 頼む店には札(依頼中 / 受領 / 入力済 / 取り消し / お断り)、遠隔の店には頼む店の呼び名。
 * 頼まれた紙は `as_remote[].prescription` を自店の一覧に混ぜ、同じ畳み方・日付の見出しに通す。
 * 入力済は輪郭のドットで沈める(消さない)。報告済みは出さない —
 * 顧客の言葉に無く、legacy の一覧にも無い(04-apps/mamoru.md「一覧の面と行の表現」)。
 *
 * 遠隔入力の 2 つ(どちらも該当する店にだけ描く。gate は「出さない」で行う):
 * - 一覧の上の [すべて | 遠隔 (新着 N)] = どこかの店の依頼先になっている店(`receives`)。毎回見る
 * - 一覧の下(「設定 / ポータルを開く」の上)の「これからの処方箋を遠隔へ」= 依頼先の設定がある店
 *   (`route`)。忙しい間だけ切り替える店の設定なので、一覧の上を取らない
 */
export const Sidebar = ({ selectedId, onSelect, onCollapse }: Props) => {
  const { data, isPending } = usePrescriptionsQuery()
  const { overview, lookup, remoteRows, newCount } = useRemoteInput()
  const { setRouteOn, isSwitchingRoute } = useRemoteInputActions()
  const [search, setSearch] = useState('')
  const [showsRemoteOnly, setShowsRemoteOnly] = useState(false)
  // カンパニーカラー/店舗ラベルは userId の静的テーブル(brandByUser)から引く。
  // 未登録ユーザーは brand が undefined になり、見た目は今まで通り変わらない。
  const brand = getBrand(localStorageWrapper.getItem('userId'))

  const route = overview?.route ?? null
  const receives = !!overview?.receives
  const remoteOnly = showsRemoteOnly && receives

  const rows = useMemo(
    () =>
      foldByOcrRequestEvent(
        remoteOnly ? remoteRows : [...(data ?? []), ...remoteRows]
      ),
    [data, remoteRows, remoteOnly]
  )
  const filtered = useMemo(
    () => rows.filter((presc) => matchesSearch(presc, search)),
    [rows, search]
  )
  const groups = useMemo(() => groupByDate(filtered), [filtered])

  return (
    <Container>
      <WorkspaceRow>
        <BrandMark />
        <Typography
          component="span"
          noWrap
          sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}
        >
          {SHORT_SERVICE_NAMES.DEFAULT}
        </Typography>
        {brand && (
          // カンパニーカラー登録ユーザーだけのバッジ。杏林堂のロゴ2色(黄地+ティール文字)を
          // そのまま組む。
          //
          // ⚠ sx の borderRadius に**数値を渡さない**。MUI が theme.shape.borderRadius(=RADIUS.md)
          // 倍するので RADIUS.lg(8) は 48px になる。px 文字列は素通しされるので必ずそちらで渡す。
          // styled() の素の CSS オブジェクトは倍されないので、そちらは数値のままでよい。
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              px: 0.75,
              py: 0.25,
              borderRadius: `${RADIUS.full}px`,
              // 字格は tag(12px)。バッジ相当の語は tag と定義済みで、micro(11px) の用途は
              // ショートカット表示・Citation に限られる(ADR-0024)。
              ...typeScale.tag,
              color: brand.accent.primary,
              background: brand.badgeBg,
            }}
          >
            {brand.label}
          </Box>
        )}
        <ButtonBase
          className="collapse-toggle"
          aria-label="一覧を格納する"
          onClick={onCollapse}
          sx={{
            width: 22,
            height: 22,
            borderRadius: `${RADIUS.sm}px`,
            opacity: 0,
            transition: 'opacity 120ms',
            color: colorTokens.fg.muted,
            '&:hover': { background: colorTokens.bg.emphasis },
            '&:focus-visible': { opacity: 1 },
          }}
        >
          <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 14 }} />
        </ButtonBase>
      </WorkspaceRow>

      {receives && (
        <RemoteFilterTabs
          showsRemoteOnly={showsRemoteOnly}
          newCount={newCount}
          onChange={setShowsRemoteOnly}
        />
      )}

      <Box sx={{ px: 1, pb: 0.5, display: 'flex', alignItems: 'center' }}>
        <InputBase
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="患者名で絞り込む"
          inputProps={{ 'aria-label': '患者名で絞り込む' }}
          sx={{
            flex: 1,
            height: 28,
            px: 1,
            borderRadius: `${RADIUS.lg}px`,
            border: `1px solid ${colorTokens.border.default}`,
            background: colorTokens.bg.default,
            fontSize: 12,
          }}
        />
        {search.trim() && (
          <Typography sx={{ ml: 1, fontSize: 12, color: colorTokens.fg.muted }}>
            {filtered.length}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {groups.map((group) => (
          <Box key={group.key} sx={{ mt: 1.5 }}>
            <Typography
              sx={{
                px: 1,
                mb: 0.25,
                fontSize: 12,
                color: colorTokens.fg.faint,
              }}
            >
              {group.label}
            </Typography>
            <RowList>
              {group.rows.map((presc) => {
                const paper = lookup(presc.ocr_request_event_id)
                return (
                  <PrescriptionRow
                    key={presc.ocr_request_event_id}
                    presc={presc}
                    isSelected={presc.ocr_request_event_id === selectedId}
                    onSelect={onSelect}
                    accent={brand?.accent.primary}
                    badge={rowBadge(paper)}
                    isSunk={isQrOnly(presc) || isSunkRow(paper)}
                  />
                )
              })}
            </RowList>
          </Box>
        ))}

        {!isPending && groups.length === 0 && (
          <Typography
            sx={{ px: 1, py: 1.5, fontSize: 13, color: colorTokens.fg.faint }}
          >
            (なし)
          </Typography>
        )}
      </Box>

      {route && (
        <RouteToggle
          isOn={route.is_on}
          disabled={isSwitchingRoute}
          onToggle={() => setRouteOn(!route.is_on)}
        />
      )}

      <FooterRow onClick={() => window.location.assign('/portal')}>
        設定 / ポータルを開く
      </FooterRow>
    </Container>
  )
}
